// scheduling/components/idd/IDDBudgetsTable.jsx
// Step 1 of the guided flow: one budget decision per job. Each row reads left to right:
// what happened last year, what we suggest, the budget now, and ONE decision (Use model /
// Keep / Use last year / Enter). A decision saves right away; Change undoes it. Clicking
// a row opens the per-task editor (the remaining hours per task).
import { Fragment, useState } from 'react';
import { ML_STATE, fmtH } from '../../utils/mlBudget';
import { isOOSTask, undoable } from '../../utils/iddSteps';
import { fmtHours, mlTooltip } from './iddFormat';
import IDDRowMenu from './IDDRowMenu';

const STATE_CHIP = {
  [ML_STATE.AGREES]: { cls: 'agrees', label: 'Agrees' },
  [ML_STATE.CLOSE]: { cls: 'close', label: 'Near model' },
  [ML_STATE.STALE]: { cls: 'stale', label: 'Stale roll-forward' },
  [ML_STATE.SET_THIS_YEAR]: { cls: 'differs', label: 'Differs from model' },
  [ML_STATE.NO_BUDGET]: { cls: 'nobudget', label: 'No budget' },
};
const KIND_LABEL = { model: 'model', keep: 'kept', py: 'last year', typed: 'entered' };
const REASONS = ['Scope change', 'Known client', 'Other'];

function optionLabel(o) {
  switch (o.kind) {
    case 'model': return `Use model ${fmtH(o.hours)}h`;
    case 'keep': return `Keep ${fmtHours(o.hours)}h`;
    case 'py': return `Use last year ${fmtHours(o.hours)}h`;
    default: return 'Enter budget';
  }
}

function optionTitle(o, info) {
  switch (o.kind) {
    case 'model': return `Set the budget to the model's ${fmtH(o.hours)}h, split across tasks the way last year's hours were (${info.ml?.split?.note || ''}). Saves right away; Change undoes it.`;
    case 'keep': return o.why
      ? `Keep ${fmtHours(o.hours)}h. The model suggests ${fmtH(info.ml?.pred?.PredHours)}h; keeping is fine when you know something it doesn't (optional: say why).`
      : `Keep ${fmtHours(o.hours)}h as this year's budget.`;
    case 'py': {
      const t = info.py?.target;
      return `Set the budget to last year's actual ${fmtH(t)}h (the Last year column), split across tasks the way last year's hours were.`
        + (t != null && Math.abs(o.hours - t) > 0.01 ? ` It comes to ${fmtHours(o.hours)}h because some tasks already have more hours logged than their share.` : '');
    }
    default: return 'Open the task list and type the remaining hours per task.';
  }
}

export default function IDDBudgetsTable({
  jobs, info, edits, selection, onToggle, onToggleAll, expanded, onExpand,
  onDecide, onSaveTyped, onCancelTyped, onEditTask, onUndo, onNotNeeded, kinds, readOnly, showManager,
  menuFor, sortCol, sortDir, onSort, busy,
}) {
  const [whyOpen, setWhyOpen] = useState(null);
  // Undecided rows tick for a decision, decided drafts for the bulk Undo.
  const canTick = (j) => !readOnly && !!info[j.PEJobIndex] && (!info[j.PEJobIndex].decided || undoable(j, 'budget'));
  const selectable = jobs.filter(canTick);
  const allOn = selectable.length > 0 && selectable.every(j => selection.has(j.PEJobIndex));
  const colCount = showManager ? 9 : 8;
  const Th = ({ col, label, style, title, center }) => (
    <th className={`idd-th${col ? '' : ' idd-th--static'}${center ? ' idd-th--center' : ''}`} style={style} title={title} onClick={col ? () => onSort(col) : undefined}>
      {label}{col && sortCol === col && <span className="idd-sort-icon">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
    </th>
  );

  return (
    <div className="idd-table-wrap">
      <table className="idd-table idd-table--steps">
        <thead>
          <tr>
            <th className="idd-th idd-th--static idd-td--check">
              <input type="checkbox" aria-label="Select all shown" checked={allOn} disabled={selectable.length === 0}
                onChange={() => onToggleAll(allOn ? [] : selectable.map(j => j.PEJobIndex))} />
            </th>
            <Th col="clientCode" label="Client" />
            <Th col="jobName" label="Job" style={{ minWidth: 170 }} />
            {showManager && <Th col="manager" label="Manager" />}
            <Th center label="Last year" title="Last year's budget → the hours it actually took (time logged as Other / out of scope left out)" />
            <Th col="budget" label="Current budget" title="This year's budget now, and how it compares with the model. Click a row to see it by task." />
            <Th label="Suggested" title="The model's number for returning 1065 / 1120 / 1120-S clients (with its likely range); last year's hours for other jobs" />
            <Th label="Decision" style={{ minWidth: 210 }} />
            <th className="idd-th idd-th--static" style={{ width: 34 }} aria-label="More" />
          </tr>
        </thead>
        <tbody>
          {jobs.length === 0 && <tr><td colSpan={colCount} className="idd-empty">Nothing to show with these filters.</td></tr>}
          {jobs.map(job => {
            const pe = job.PEJobIndex;
            const r = info[pe];
            if (!r) return null;
            const dirty = !!edits[pe]?.tasks;
            const isOpen = expanded === pe;
            const ml = r.ml;
            const chip = ml ? STATE_CHIP[ml.state] : null;
            const shownBudget = dirty ? r.eff : r.decidedHours ?? r.base;
            const kind = kinds[pe];
            const opts = r.options;
            return (
              <Fragment key={pe}>
                <tr
                  className={`idd-row idd-row--job${r.ready ? ' idd-row--ready' : ''}${dirty ? ' idd-row--dirty' : ''}${isOpen ? ' idd-row--expanded' : ''}`}
                  onClick={() => onExpand(pe)}
                >
                  <td className="idd-td idd-td--check" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" aria-label={`Select ${job.ClientCode}`} checked={selection.has(pe)}
                      disabled={!canTick(job)} onChange={() => onToggle(pe)} />
                  </td>
                  <td className="idd-td">
                    <div className="idd-cell-main">{job.ClientCode}</div>
                    <div className="idd-cell-sub">{job.ClientName}</div>
                  </td>
                  <td className="idd-td">
                    <div className="idd-cell-main">{job.JobName}</div>
                    {job.ServiceDesc && <div className="idd-cell-sub">{job.ServiceDesc}</div>}
                  </td>
                  {showManager && <td className="idd-td">{job.ManagerName}</td>}
                  <td className="idd-td idd-td--num idd-td--center" title={r.lastYear.fromModel ? "Last year's job has no task data here yet, so 'actual' is the in-scope hours the model saw." : undefined}>
                    {r.lastYear.budget == null && r.lastYear.actual == null ? <span className="idd-ly idd-ly--none">none</span> : (
                      // A fixed grid, so every row's numbers and labels line up down the column.
                      <span className="idd-ly">
                        <span className="idd-ly-num">{r.lastYear.budget != null ? `${fmtHours(r.lastYear.budget)}h` : '—'}</span>
                        <span className="idd-ly-arrow">→</span>
                        <span className="idd-ly-num">{r.lastYear.actual != null ? `${fmtH(r.lastYear.actual)}h` : '—'}</span>
                        <span className="idd-ly-lbl">budget</span>
                        <span />
                        <span className="idd-ly-lbl">actual</span>
                      </span>
                    )}
                  </td>
                  <td className="idd-td idd-td--num">
                    {dirty && Math.abs(r.eff - r.base) > 0.05 ? (
                      <><span className="idd-budget-orig">{fmtHours(r.base)}</span> → <strong>{fmtHours(r.eff)}h</strong></>
                    ) : shownBudget > 0 ? <strong>{fmtHours(shownBudget)}h</strong> : <span className="idd-cell-sub">none</span>}
                    {(job.CYActualHours || 0) > 0 && <div className="idd-cell-sub">{fmtHours(job.CYActualHours)}h so far</div>}
                    {/* The label describes this budget against the model's number. */}
                    {chip && <div><span className={`idd-ml-chip idd-ml-chip--${chip.cls}`} title={mlTooltip(ml)}>{chip.label}</span></div>}
                  </td>
                  <td className="idd-td idd-td--num">
                    {ml ? (
                      <span title={mlTooltip(ml)}>
                        <span className="idd-sugg-main">{fmtH(ml.pred.PredHours)}h</span>
                        <span className="idd-cell-sub"> {fmtH(ml.pred.Lo50)}–{fmtH(ml.pred.Hi50)}</span>
                        {ml.stub && <span className="idd-ml-stub-tag">STUB</span>}
                        <div><span className="idd-src-chip idd-src-chip--model">model</span></div>
                      </span>
                    ) : r.py ? (
                      <span title="Last year's actual hours, task by task">
                        <span className="idd-sugg-main">{fmtHours(r.py.total)}h</span>
                        <div><span className="idd-src-chip idd-src-chip--py">last year</span></div>
                      </span>
                    ) : <span className="idd-cell-sub">—</span>}
                  </td>
                  <td className="idd-td idd-td--decision" onClick={e => e.stopPropagation()}>
                    {job.ReviewStatus === 'submitted' ? <span className="idd-dec-done">Submitted</span>
                      : job.RemoveAction ? <span className="idd-dec-removed">{job.RemoveAction === 'close_no_roll' ? 'Closed, not rolling' : 'Not doing this year'}</span>
                      : readOnly ? <span className="idd-dec-done">{fmtHours(shownBudget)}h</span>
                      : dirty ? (
                        <span className="idd-dec-group">
                          <button className="idd-dec-btn idd-dec-btn--budget idd-dec-btn--primary" disabled={busy}
                            onClick={() => onSaveTyped(pe)}>Save {fmtHours(r.eff)}h</button>
                          <button className="idd-dec-link" onClick={() => onCancelTyped(pe)}>Cancel</button>
                        </span>
                      ) : r.decided ? (
                        <span className="idd-dec-group">
                          <span className="idd-dec-done">
                            ✓ {job.ReviewStatus === 'confirmed' ? 'Confirmed' : `${fmtHours(shownBudget)}h`}
                            {kind && <span className="idd-dec-kind"> · {KIND_LABEL[kind] || kind}</span>}
                          </span>
                          {job.ReviewStatus === 'draft' && (
                            <button className="idd-dec-link" disabled={busy} onClick={() => onUndo(pe)} title="Undo this budget decision">Change</button>
                          )}
                        </span>
                      ) : (
                        <span className="idd-dec-group">
                          {opts.map((o, i) => (
                            <Fragment key={o.kind}>
                              <button
                                className={`idd-dec-btn idd-dec-btn--budget${i === 0 ? ' idd-dec-btn--primary' : ''}`}
                                disabled={busy}
                                title={optionTitle(o, r)}
                                onClick={() => (o.kind === 'enter' ? onExpand(pe, true) : onDecide(pe, o))}
                              >
                                {optionLabel(o)}
                              </button>
                              {o.kind === 'keep' && o.why && (
                                <button className="idd-dec-link" onClick={() => setWhyOpen(w => (w === pe ? null : pe))}
                                  title="Optional: say why you're keeping it (helps check the model after the season)">why?</button>
                              )}
                            </Fragment>
                          ))}
                          {r.lastYearEmpty && onNotNeeded && (
                            <button className="idd-dec-link" onClick={() => onNotNeeded(pe)}
                              title="Last year's job closed with no hours logged. Not doing it this year, or close it and don't roll it forward?">
                              Not needed?
                            </button>
                          )}
                          {whyOpen === pe && (
                            <span className="idd-dec-reasons">
                              {REASONS.map(reason => (
                                <button key={reason} className="idd-ml-reason-chip" disabled={busy}
                                  onClick={() => { setWhyOpen(null); onDecide(pe, opts.find(o => o.kind === 'keep'), { reason }); }}>
                                  {reason}
                                </button>
                              ))}
                            </span>
                          )}
                        </span>
                      )}
                  </td>
                  <td className="idd-td" onClick={e => e.stopPropagation()}><IDDRowMenu items={menuFor(job)} /></td>
                </tr>
                {isOpen && (
                  <tr className="idd-task-editor-row">
                    <td colSpan={colCount} className="idd-task-editor-cell">
                      <TaskEditor job={job} r={r} edits={edits[pe]?.tasks || {}} readOnly={readOnly || r.locked}
                        onEditTask={(subject, v) => onEditTask(pe, subject, v)} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TaskEditor({ job, r, edits, readOnly, onEditTask }) {
  const rows = r.rows || [];
  if (rows.length === 0) return <div className="idd-cell-sub">No task budgets synced for this job yet.</div>;
  const split = r.ml?.split?.budgets || null;
  const moved199A = rows.reduce((s, t) => s + (Number(t.Moved199A) || 0), 0);
  return (
    <div className="idd-task-editor">
      <div className="idd-task-editor-note">
        Budget = hours so far + remaining. Change the remaining hours on any task, then click <b>Save</b> in the Decision column.
        {r.ml && <> The model's {fmtH(r.ml.pred.PredHours)}h is split the way last year's hours were.</>}
        {moved199A > 0 && <> We no longer budget Section 199A: its {fmtHours(moved199A)}h is now on Preparation.</>}
      </div>
      <table className="idd-task-table">
        <thead>
          <tr>
            <th>Task</th><th>Hours so far</th><th>Last year</th>{split && <th>Model share</th>}<th>Budget now</th><th>Remaining</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(t => {
            const oos = isOOSTask(t);
            const orig = (Number(t.BudgetHours) || 0) - (Number(t.WIPHours) || 0);
            const saved = t.SavedRemainingHrs;
            const edited = edits[t.TaskSubject];
            const value = edited != null ? edited : saved != null ? Number(saved) : orig;
            // An untouched zero is an empty box with a gray "0", so clicking in and typing 5
            // gives 5 (a real 0 in the box couldn't be deleted). Cleared boxes stay empty
            // and count as 0.
            const shown = edited == null && Number(value) === 0 ? '' : value;
            return (
              <tr key={t.TaskSubject} className={oos ? 'idd-task-row--oos' : undefined}>
                <td>{oos ? <span title="Time logged as Out of Scope or with no task. Not editable.">Other (out of scope)</span> : t.TaskSubject}</td>
                <td>{fmtHours(t.WIPHours || 0)}</td>
                <td>{t.PYWIP != null ? `${fmtHours(t.PYWIP)}h` : '—'}</td>
                {split && <td>{split[t.TaskSubject] != null ? `${fmtH(split[t.TaskSubject])}h` : '—'}</td>}
                <td>{fmtHours(saved != null ? Number(saved) + (Number(t.WIPHours) || 0) : t.BudgetHours || 0)}</td>
                <td>
                  {oos ? '—' : (
                    <input type="number" min="0" step="0.5" className="idd-input idd-input--num"
                      value={shown} placeholder="0" disabled={readOnly} aria-label={`Remaining hours for ${t.TaskSubject}`}
                      onChange={e => onEditTask(t.TaskSubject, e.target.value === '' ? '' : Number(e.target.value))} />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

